<template>
<div
    :class="bem([
        {
            disabled: inputDisabled,
            exceed: inputExceed,
            prefix: $slots.prefix || prefixIcon,
            suffix: $slots.suffix || suffixIcon || clearable || showPassword,
            group: $slots.prepend || $slots.append,
            append: $slots.append,
            focus: focused,
            prepend: $slots.prepend,
        },
        type === 'textarea' ? 'textarea' : 'normal',
        inputSize ? inputSize : ''
    ])"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false"
>
    <template v-if="type !== 'textarea'">
        <!-- 前置元素 -->
        <div :class="bem('prepend')" v-if="$slots.prepend">
            <slot name="prepend"  />
        </div>
        <input
            :list="dataListId"
            :tabindex="tabindex"
            v-if="type !== 'textarea'"
            :class="bem('inner')"
            v-bind="$attrs"
            :type="showPassword ? (passwordVisible ? 'text': 'password') : type"
            :disabled="inputDisabled"
            :readonly="readonly"
            :autocomplete="autocomplete"
            ref="input"
            @compositionstart="handleCompositionStart"
            @compositionend="handleCompositionEnd"
            @input="handleInput"
            @focus="handleFocus"
            @blur="handleBlur"
            @change="handleChange"
            :aria-label="label"
        >
        <!-- 前置内容 -->
        <span :class="bem('prefix')" v-if="$slots.prefix || prefixIcon">
            <slot name="prefix" />
            <Icon :class="bem('icon')" v-if="prefixIcon" :name="prefixIcon" />
        </span>
        <!-- 后置内容 -->
        <span :class="bem('suffix')" v-if="getSuffixVisible()">
            <span :class="bem('suffix-inner')">
                <template v-if="!showClear || !showPwdVisible || !isWordLimitVisible">
                    <slot name="suffix" />
                    <Icon :class="bem('icon')" v-if="suffixIcon" :name="suffixIcon" />
                </template>
                <Icon
                    v-if="showClear"
                    :class="[bem('icon'), bem('clear')]"
                    name="gdd-clear_outline"
                    @click.native="clear"
                />
                <Icon
                    v-if="showPwdVisible"
                    :class="[bem('icon'), bem('clear')]"
                    name="gdd-pic-camera"
                    @click.native="handlePasswordVisible"
                />
                <span v-if="isWordLimitVisible" :class="bem('count')">
                    <span :class="bem('count-inner')">{{ textLength }}/{{ upperLimit }}</span>
                </span>
            </span>
            <Icon
                :class="[bem('icon'), bem('validateIcon')]"
                v-if="validateState"
                :name="validateIcon"
            />
        </span>
        <!-- 后置元素 -->
        <div :class="bem('append')" v-if="$slots.append">
            <slot name="append" />
        </div>
    </template>
    <textarea
        v-else
        :tabindex="tabindex"
        :class="bem('inner')"
        @compositionstart="handleCompositionStart"
        @compositionend="handleCompositionEnd"
        @input="handleInput"
        ref="textarea"
        v-bind="$attrs"
        :disabled="inputDisabled"
        :readonly="readonly"
        :autocomplete="autocomplete"
        :style="textareaStyle"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        :aria-label="label"
    />
    <span
        v-if="isWordLimitVisible && type === 'textarea'"
        :class="bem('count')"
    >{{ textLength }}/{{ upperLimit }}</span>
</div>

</template>
<script>
import emitter2 from "../utils/emitter";
import calcTextareaHeight2 from "./calcTextareaHeight";
import {bemMixin} from "../utils";
import Icon from "../icon";
function merge(target) {
  for (let i = 1, j = arguments.length; i < j; i++) {
    let source = arguments[i] || {};
    for (let prop in source) {
      if (source.hasOwnProperty(prop)) {
        let value = source[prop];
        if (value !== void 0) {
          target[prop] = value;
        }
      }
    }
  }
  return target;
}
export default {
  name: "g-input",
  componentName: "GInput",
  components: {
    Icon
  },
  mixins: [emitter2, bemMixin],
  inheritAttrs: false,
  inject: {
    gForm: {
      default: ""
    },
    gFormItem: {
      default: ""
    }
  },
  props: {
    value: [String, Number],
    size: String,
    resize: String,
    form: String,
    disabled: Boolean,
    readonly: Boolean,
    type: {
      type: String,
      default: "text"
    },
    autosize: {
      type: [Boolean, Object],
      default: false
    },
    autocomplete: {
      type: String,
      default: "off"
    },
    validateEvent: {
      type: Boolean,
      default: true
    },
    suffixIcon: String,
    prefixIcon: String,
    label: String,
    clearable: {
      type: Boolean,
      default: false
    },
    showPassword: {
      type: Boolean,
      default: false
    },
    showWordLimit: {
      type: Boolean,
      default: false
    },
    dataListId: String,
    tabindex: String
  },
  data() {
    return {
      textareaCalcStyle: {},
      hovering: false,
      focused: false,
      isComposing: false,
      passwordVisible: false
    };
  },
  computed: {
    _gFormItemSize() {
      return (this.gFormItem || {}).gFormItemSize;
    },
    validateState() {
      return this.gFormItem ? this.gFormItem.validateState : "";
    },
    needStatusIcon() {
      return this.gForm ? this.gForm.statusIcon : false;
    },
    validateIcon() {
      return {
        validating: "gdd-loading",
        success: "gdd-check-circle-solid",
        error: "gdd-close-circle-fill"
      }[this.validateState];
    },
    textareaStyle() {
      return merge({}, this.textareaCalcStyle, {resize: this.resize});
    },
    inputSize() {
      return this.size || this._gFormItemSize || (this.$ELEMENT || {}).size;
    },
    inputDisabled() {
      return this.disabled || (this.gForm || {}).disabled;
    },
    nativeInputValue() {
      return this.value === null || this.value === void 0 ? "" : String(this.value);
    },
    showClear() {
      return this.clearable && !this.inputDisabled && !this.readonly && this.nativeInputValue && (this.focused || this.hovering);
    },
    showPwdVisible() {
      return this.showPassword && !this.inputDisabled && !this.readonly && (!!this.nativeInputValue || this.focused);
    },
    isWordLimitVisible() {
      return this.showWordLimit && this.$attrs.maxlength && (this.type === "text" || this.type === "textarea") && !this.inputDisabled && !this.readonly && !this.showPassword;
    },
    upperLimit() {
      return this.$attrs.maxlength;
    },
    textLength() {
      if (typeof this.value === "number") {
        return String(this.value).length;
      }
      return (this.value || "").length;
    },
    inputExceed() {
      return this.isWordLimitVisible && this.textLength > this.upperLimit;
    }
  },
  watch: {
    value(val) {
      this.$nextTick(this.resizeTextarea);
      if (this.validateEvent) {
        this.dispatch("GFormItem", "g.form.change", [val]);
      }
    },
    nativeInputValue() {
      this.setNativeInputValue();
    },
    type() {
      this.$nextTick(() => {
        this.setNativeInputValue();
        this.resizeTextarea();
        this.updateIconOffset();
      });
    }
  },
  created() {
    this.$on("inputSelect", this.select);
  },
  mounted() {
    this.setNativeInputValue();
    this.resizeTextarea();
    this.updateIconOffset();
  },
  updated() {
    this.$nextTick(this.updateIconOffset);
  },
  methods: {
    focus() {
      this.getInput().focus();
    },
    blur() {
      this.getInput().blur();
    },
    handleBlur(event) {
      this.focused = false;
      this.$emit("blur", event);
      if (this.validateEvent) {
        this.dispatch("GFormItem", "g.form.blur", [this.value]);
      }
    },
    select() {
      this.getInput().select();
    },
    resizeTextarea() {
      if (this.$isServer)
        return;
      const {autosize, type} = this;
      if (type !== "textarea")
        return;
      if (!autosize) {
        this.textareaCalcStyle = {
          minHeight: calcTextareaHeight2(this.$refs.textarea).minHeight
        };
        return;
      }
      const minRows = autosize.minRows;
      const maxRows = autosize.maxRows;
      this.textareaCalcStyle = calcTextareaHeight2(this.$refs.textarea, minRows, maxRows);
    },
    setNativeInputValue() {
      const input = this.getInput();
      if (!input)
        return;
      if (input.value === this.nativeInputValue)
        return;
      input.value = this.nativeInputValue;
    },
    handleFocus(event) {
      this.focused = true;
      this.$emit("focus", event);
    },
    handleCompositionStart() {
      this.isComposing = true;
    },
    handleCompositionEnd(event) {
      this.isComposing = false;
      this.handleInput(event);
    },
    handleInput(event) {
      if (this.isComposing)
        return;
      if (event.target.value === this.nativeInputValue)
        return;
      this.$emit("input", event.target.value);
      this.$nextTick(this.setNativeInputValue);
    },
    handleChange(event) {
      this.$emit("change", event.target.value);
    },
    calcIconOffset(place) {
      let elList = [].slice.call(this.$el.querySelectorAll(`.el-input__${place}`) || []);
      if (!elList.length)
        return;
      let el = null;
      for (let i = 0; i < elList.length; i++) {
        if (elList[i].parentNode === this.$el) {
          el = elList[i];
          break;
        }
      }
      if (!el)
        return;
      const pendantMap = {
        suffix: "append",
        prefix: "prepend"
      };
      const pendant = pendantMap[place];
      if (this.$slots[pendant]) {
        el.style.transform = `translateX(${place === "suffix" ? "-" : ""}${this.$el.querySelector(`.el-input-group__${pendant}`).offsetWidth}px)`;
      } else {
        el.removeAttribute("style");
      }
    },
    updateIconOffset() {
      this.calcIconOffset("prefix");
      this.calcIconOffset("suffix");
    },
    clear() {
      this.$emit("input", "");
      this.$emit("change", "");
      this.$emit("clear");
    },
    handlePasswordVisible() {
      this.passwordVisible = !this.passwordVisible;
      this.focus();
    },
    getInput() {
      return this.$refs.input || this.$refs.textarea;
    },
    getSuffixVisible() {
      return this.$slots.suffix || this.suffixIcon || this.showClear || this.showPassword || this.isWordLimitVisible || this.validateState && this.needStatusIcon;
    }
  }
};

</script>
<style>
.g-input {
  position: relative;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  width: 100%;
  border-radius: 4px;
  border: 1px solid #dadde1;
}
.g-input--focus {
  background-color: #fff;
  border-color: #1B6DE0;
  box-shadow: 0px 0px 4px 0px rgba(27, 109, 224, 0.3);
}
.g-input .g-input__clear {
  color: #939ba6;
  cursor: pointer;
  transition: color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.g-input .g-input__clear:hover {
  color: #2254f4;
}
.g-input--normal .g-input__count {
  height: 100%;
  display: inline-flex;
  align-items: center;
  color: #8d949e;
  font-size: 12px;
}
.g-input--normal .g-input__count .g-input__count-inner {
  background: #ffffff;
  line-height: initial;
  display: inline-block;
  padding: 0 5px;
}
.g-input__inner {
  -webkit-appearance: none;
  background-color: #fff;
  background-image: none;
  border: none;
  box-sizing: border-box;
  color: #000;
  display: inline-block;
  border-radius: 4px;
  font-size: inherit;
  height: 38px;
  outline: none;
  padding: 0px 15px;
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  flex: 1 1 auto;
  width: 100%;
}
.g-input__inner::placeholder {
  color: #8D949E;
}
.g-input__inner:hover {
  border-color: #bec3c9;
}
.g-input__inner:focus {
  outline: none;
}
.g-input__prepend {
  padding-left: 15px;
}
.g-input--append .g-input__inner {
  padding-right: 12px;
}
.g-input--prepend .g-input__inner {
  padding-left: 12px;
}
.g-input__append {
  padding-right: 15px;
}
.g-input__suffix {
  position: absolute;
  height: 100%;
  right: 5px;
  top: 0;
  color: #939ba6;
  transition: all 0.3s;
  pointer-events: none;
}
.g-input__suffix-inner {
  pointer-events: all;
}
.g-input__prefix {
  position: absolute;
  height: 100%;
  left: 5px;
  top: 0;
  text-align: center;
  color: #939ba6;
  transition: all 0.3s;
}
.g-input__icon {
  height: 100%;
  transition: all 0.3s;
  font-size: 20px;
}
.g-input__validateIcon {
  pointer-events: none;
}
.g-input--active .g-input__inner {
  outline: none;
  border-color: #1B6DE0;
}
.g-input--disabled .g-input__inner {
  background-color: #e0e5ea;
  border-color: #e0e5ea;
  color: #8D949E;
  cursor: not-allowed;
}
.g-input--disabled .g-input__inner::placeholder {
  color: #8D949E;
}
.g-input--disabled .g-input__icon {
  cursor: not-allowed;
}
.g-input--exceed .g-input__inner {
  border-color: #de415c;
}
.g-input--exceed .g-input__suffix .g-input__count {
  color: #de415c;
}
.g-input--suffix .g-input__inner {
  padding-right: 30px;
}
.g-input--prefix .g-input__inner {
  padding-left: 30px;
}
.g-input--medium {
  font-size: 14px;
}
.g-input--medium .g-input__inner {
  height: 36px;
  line-height: 36px;
}
.g-input--medium .g-input__icon {
  line-height: 36px;
}
.g-input--small {
  font-size: 14px;
}
.g-input--small .g-input__inner {
  height: 32px;
  line-height: 32px;
}
.g-input--small .g-input__icon {
  line-height: 32px;
}
.g-input--mini {
  font-size: 12px;
}
.g-input--mini .g-input__inner {
  height: 28px;
  line-height: 28px;
}
.g-input--mini .g-input__icon {
  line-height: 28px;
}
.g-input input:-webkit-autofill {
  box-shadow: inset 0 0 0 1000px #fff;
}
.g-input input[type=text]:focus,
.g-input input[type=password]:focus,
.g-input textarea:focus {
  box-shadow: 0 0 0 1000px inset inset;
}
.g-input--textarea {
  position: relative;
  display: inline-block;
  width: 100%;
  vertical-align: bottom;
  font-size: 14px;
}
.g-input--textarea .g-input__inner {
  display: block;
  resize: vertical;
  padding: 5px 15px;
  line-height: 1.5;
  box-sizing: border-box;
  height: auto;
  width: 100%;
  font-size: inherit;
  color: #000;
  background-color: #fff;
  background-image: none;
  border: 1px solid #dadde1;
  border-radius: 4px;
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.g-input--textarea .g-input__inner::placeholder {
  color: #8D949E;
}
.g-input--textarea .g-input__inner:hover {
  border-color: #bec3c9;
}
.g-input--textarea .g-input__inner:focus {
  outline: none;
  border-color: #1B6DE0;
}
.g-input--textarea .g-input__count {
  color: #8d949e;
  background: #fff;
  position: absolute;
  font-size: 12px;
  bottom: 5px;
  right: 10px;
}
.g-input--textarea .g-input--disabled .g-input--textarea__inner {
  background-color: #e0e5ea;
  border-color: #e0e5ea;
  color: #8D949E;
  cursor: not-allowed;
}
.g-input--textarea .g-input--disabled .g-input--textarea__inner::placeholder {
  color: #8D949E;
}
.g-input--textarea .g-input--exceed .el-textarea__inner {
  border-color: #de415c;
}
.g-input--textarea .g-input--exceed .el-input__count {
  color: #de415c;
}
/** disalbe default clear on IE */
.g-input__inner::-ms-clear {
  display: none;
  width: 0;
  height: 0;
}

</style>
